import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { SharedService } from 'src/app/core/services/shared.service';
import Swal from 'sweetalert2';

/**
 * Columna dinamica. La construye el backend al vuelo segun groupBy.
 *   - modo 'ESTADO'  : key = PRO31COD, label = PRO31DES, color = PRO31COL, etc.
 *   - modo 'USUARIO' : key = S00USR,    label = S00NOM,   color = ''
 */
interface ColumnaBoard {
  key:   string;
  label: string;
  cat:   string;        // BACKLOG | PROCESS | DONE | CANCEL
  color: string;
  wip:   number | null;
  ord:   number;
}

interface TarjetaBoard {
  PRO23COD: number;
  PRO23TIT: string;
  PRO23DES: string;
  S00USR:   string;
  PRO23PRI: string;
  PRO23FVE: string;
  PRO23FEC: string;
  PRO23AUT: string;

  PRO30COD: string;
  PRO30DES: string;
  PRO30COL: string;
  PRO30ICO: string;

  PRO31COD: string;
  PRO23EPI: string;
  PRO23REP: string;
  PRO32COD: string;
  PRO23SP:  number;
  PRO23ETO: number;
  PRO23ERE: number;
  PRO23ELP: number;
  PRO23PRG: number;
  PRO23SEV: string;
  PRO23AMB: string;
  PRO01COD: string;
  PRO48ORD: number;
  // index signature para el accesso dinamico por this.campoGrupo()
  [key: string]: any;
}

interface SprintCombo {
  PRO32COD: string;
  PRO32NOM: string;
  PRO32EST: string;
  PRO32FEI?: string;
  PRO32FEF?: string;
}

interface SprintActual {
  PRO32COD: string;
  PRO32NOM: string;
  PRO32GOL?: string;
  PRO32FEI?: string;
  PRO32FEF?: string;
  PRO32EST?: string;
  PRO32CAP?: number;
  PRO32SPC?: number;
}

interface TotalCol {
  key: string;
  label: string;
  cat: string;
  count: number;
  sp: number;
}

interface Totales {
  porColumna: TotalCol[];
  totalCards: number;
  totalSP: number;
  doneCards: number;
  doneSP: number;
}

interface PermisosBoard {
  CREATE_CARD: string;
  MOVE_CARD: string;
  EDIT_CARD: string;
  DELETE_CARD: string;
  ATTACH_CARD: string;
  DETACH_CARD: string;
  CLOSE_SPRINT: string;
  VIEW_REPORTS: string;
}

interface CardDisponible {
  PRO23COD: number;
  PRO23TIT: string;
  PRO31COD: string;
  PRO30COD: string;
  PRO01COD: string;
  S00USR:   string;
}


/**
 * Componente nativo del Tablero (PRO Board) con Angular CDK DragDrop.
 *
 * Se monta dentro de la cejilla SYS16EVE = PRO_BOARD del mantenimiento_wizard
 * de PRO01ARC. Recibe la fila seleccionada via @Input() llaves.
 *
 * Modelo v2:
 *   - Columnas DINAMICAS por estado (PRO31COD) o por usuario (S00USR).
 *   - Cards COMPARTIDAS entre tableros via PRO48ARC.
 *   - Estado y asignado de la card son GLOBALES.
 *
 * v3 (2026-06-10): Migrado de Syncfusion Kanban a Angular CDK DragDrop.
 *   - Sin librerias externas pesadas (Syncfusion ~150KB -> CDK ~30KB).
 *   - Sin overrides de selectores .e-*.
 *   - Sin ViewEncapsulation.None.
 */
@Component({
  selector: 'app-pro-board',
  templateUrl: './board.html',
  styleUrls: ['./board.scss']
})
export class BoardComponent implements OnInit {

  @Input() WsLlaves: any;
  @Output() cancelar = new EventEmitter<void>();

  // Endpoints (mismo patron que Notas / Ausencias).
  private urlSnapshot    = 'api/ws/pro/Board/Snapshot/proBoard';
  private urlListSprint  = 'api/ws/pro/Board/ListarSprints/proBoard';
  private urlMover       = 'api/ws/pro/Board/MoverTarjeta/proBoard';
  private urlQuickAdd    = 'api/ws/pro/Board/QuickAdd/proBoard';
  private urlAttach      = 'api/ws/pro/Board/AgregarCardATablero/proBoard';
  private urlDetach      = 'api/ws/pro/Board/QuitarCardDelTablero/proBoard';
  private urlBuscar      = 'api/ws/pro/Board/BuscarCardsDisponibles/proBoard';
  private urlIniciar     = 'api/ws/pro/Board/IniciarSprint/proBoard';
  private urlCerrar      = 'api/ws/pro/Board/CerrarSprint/proBoard';

  // Llaves.
  PRO01COD = '';
  PRO21COD = '';
  PRO32COD = '';
  groupBy: 'ESTADO' | 'USUARIO' = 'ESTADO';

  // Datos del backend.
  header: any = { PRO01DES: '', PRO21NOM: '' };
  sprintActual: SprintActual = { PRO32COD: '', PRO32NOM: '' };
  sprints: SprintCombo[] = [];
  columnas: ColumnaBoard[] = [];
  tarjetas: TarjetaBoard[] = [];
  totales: Totales = { porColumna: [], totalCards: 0, totalSP: 0, doneCards: 0, doneSP: 0 };
  permisos: PermisosBoard = {
    CREATE_CARD: 'N', MOVE_CARD: 'N', EDIT_CARD: 'N',
    DELETE_CARD: 'N', ATTACH_CARD: 'N', DETACH_CARD: 'N',
    CLOSE_SPRINT: 'N', VIEW_REPORTS: 'N'
  };

  // Filtros y vista.
  soloMias = false;
  vistaCompacta = false;
  swimlane = '';
  busqueda = '';
  cargando = false;

  // Quick add por columna.
  quickAddColumnKey: string | null = null;
  quickAddText = '';

  // Modal "agregar card existente".
  modalAttachVisible = false;
  modalAttachQ = '';
  modalAttachSoloProy = true;
  modalAttachResults: CardDisponible[] = [];
  modalAttachBuscando = false;


  constructor(private dataService: SharedService) { }


  // ===================================================================
  // Ciclo de vida y carga inicial
  // ===================================================================

  ngOnInit(): void {
    this.PRO01COD = this.WsLlaves[0].PRO01COD;
    this.PRO21COD = this.WsLlaves[0].PRO21COD;
    this.PRO32COD = this.WsLlaves[0].PRO32COD;
    // if (ll.groupBy === 'USUARIO' || ll.groupBy === 'ESTADO') this.groupBy = ll.groupBy;
    this.groupBy = 'ESTADO';

    if (!this.PRO01COD) {
      Swal.fire('Atencion', 'No se recibio el codigo del proyecto (PRO01COD).', 'warning');
      return;
    }
    this.cargarSnapshot();
  }

  cargarSnapshot() {
    this.cargando = true;
    const body = {
      LLAVES: [{
        PRO01COD: this.PRO01COD,
        PRO21COD: this.PRO21COD,
        PRO32COD: this.PRO32COD,
        groupBy:  this.groupBy,
        soloMias: this.soloMias
      }]
    };
    this.dataService.ConsultarWS(this.urlSnapshot, JSON.stringify(body)).subscribe(
      (resp: any) => {
        const d = resp?.DATA || {};
        this.header       = d.Header       || { PRO01DES: '', PRO21NOM: '' };
        this.sprintActual = d.SprintActual || { PRO32COD: '', PRO32NOM: '' };
        this.sprints      = d.Sprints      || [];
        this.groupBy      = (d.GroupBy === 'USUARIO') ? 'USUARIO' : 'ESTADO';
        this.columnas     = d.Columnas     || [];
        this.tarjetas     = d.Tarjetas     || [];
        this.totales      = d.Totales      || this.totales;
        this.permisos     = d.Permisos     || this.permisos;

        this.PRO21COD = this.header?.PRO21COD || this.PRO21COD;
        if (this.PRO32COD === '' && this.sprintActual?.PRO32COD) {
          this.PRO32COD = this.sprintActual.PRO32COD;
        }

        this.cargando = false;
      },
      (err: any) => {
        this.cargando = false;
        Swal.fire('Error', err || 'No fue posible cargar el tablero.', 'error');
      }
    );
  }


  /** Campo de la card que define a que "columna" pertenece. */
  campoGrupo(): string {
    return this.groupBy === 'USUARIO' ? 'S00USR' : 'PRO31COD';
  }


  /** IDs de todas las dropList (necesario para que se conecten entre si). */
  dropListIds(): string[] {
    return this.columnas.map(c => this.dropListId(c.key));
  }

  /** Construye un ID predecible para cada columna (CDK requiere ids unicos). */
  dropListId(colKey: string): string {
    return 'pb-col-' + (colKey || '__na__');
  }


  // ===================================================================
  // Toolbar
  // ===================================================================

  cambiarGroupBy(value: 'ESTADO' | 'USUARIO') {
    if (value !== 'ESTADO' && value !== 'USUARIO') return;
    this.groupBy = value;
    this.cargarSnapshot();
  }

  cambiarSprint(value: string) {
    this.PRO32COD = value == null ? '' : String(value);
    this.cargarSnapshot();
  }

  toggleSoloMias() {
    this.soloMias = !this.soloMias;
    this.cargarSnapshot();
  }

  cambiarSwimlane(value: string) {
    this.swimlane = value || '';
    // Swimlane sigue siendo opcional; lo implementaremos en una segunda iteracion
    // cuando confirmemos el uso. Por ahora no afecta el render.
  }

  refrescar() {
    this.cargarSnapshot();
  }


  // ===================================================================
  // Drag & drop con Angular CDK
  // ===================================================================

  /** Devuelve las cards de una columna (filtradas por el campo de agrupacion). */
  cardsDeColumna(colKey: string): TarjetaBoard[] {
    const campo = this.campoGrupo();
    return this.tarjetas
      .filter(t => String((t as any)[campo] || '') === colKey)
      .sort((a, b) => (a.PRO48ORD || 0) - (b.PRO48ORD || 0));
  }


  onDrop(event: CdkDragDrop<TarjetaBoard[]>, colDestino: ColumnaBoard) {
    if (!this.PRO21COD) return;

    if (this.permisos.MOVE_CARD !== 'S') {
      Swal.fire('Atencion', 'No tiene permiso para mover tarjetas.', 'warning');
      return;
    }

    const campo = this.campoGrupo();
    const card  = event.item.data as TarjetaBoard;
    const colOrigenKey  = String(card[campo] || '');
    const colDestinoKey = colDestino.key;
    const mismaColumna  = (event.previousContainer === event.container);

    // Reordenar localmente para feedback inmediato.
    if (mismaColumna) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      // Movemos el item entre arrays locales y le seteamos el nuevo valor de columna.
      card[campo] = colDestinoKey;
      event.previousContainer.data.splice(event.previousIndex, 1);
      event.container.data.splice(event.currentIndex, 0, card);
    }

    // Validacion local opcional de WIP (solo en modo ESTADO).
    if (!mismaColumna && this.groupBy === 'ESTADO' && colDestino.wip != null) {
      const cnt = event.container.data.length;
      if (cnt > colDestino.wip) {
        // Soft block: dejamos que el backend tambien valide y rechace.
        // Si rechaza, el snapshot revierte visualmente.
      }
    }

    // Recalcular PRO48ORD local para que la UI no salte al recargar.
    event.container.data.forEach((c, i) => { c.PRO48ORD = (i + 1) * 10; });

    // Orden de la columna destino (lista de PRO23COD en el nuevo orden).
    const orden = event.container.data.map(c => c.PRO23COD);

    const body = {
      LLAVES: [{
        PRO21COD:     this.PRO21COD,
        PRO23COD:     card.PRO23COD,
        campo:        campo,
        valorNuevo:   colDestinoKey,
        ordenColumna: orden
      }]
    };
    this.dataService.ConsultarWS(this.urlMover, JSON.stringify(body)).subscribe(
      (resp: any) => {
        const d = resp?.DATA || {};
        if (d.ok === false) {
          Swal.fire('Atencion', d.msg || 'No se pudo mover la tarjeta.', 'warning');
        this.cargarSnapshot();
        }
      },
      (err: any) => {
        Swal.fire('Error', err || 'No se pudo mover la tarjeta.', 'error');
        this.cargarSnapshot();
      }
    );
  }


  // ===================================================================
  // Quick add
  // ===================================================================

  abrirQuickAdd(col: ColumnaBoard) {
    if (this.permisos.CREATE_CARD !== 'S') return;
    this.quickAddColumnKey = col.key;
    this.quickAddText = '';
  }

  cancelarQuickAdd() {
    this.quickAddColumnKey = null;
    this.quickAddText = '';
  }

  confirmarQuickAdd() {
    if (this.quickAddColumnKey === null) { this.cancelarQuickAdd(); return; }
    if (!this.quickAddText.trim()) { this.cancelarQuickAdd(); return; }
    const sprintAGuardar = (this.PRO32COD === '' || this.PRO32COD === '-1') ? '' : this.PRO32COD;
    const body = {
      LLAVES: [{
        PRO21COD: this.PRO21COD,
        PRO01COD: this.PRO01COD,
        PRO23TIT: this.quickAddText.trim(),
        PRO30COD: 'TASK',
        PRO31COD: this.groupBy === 'ESTADO'  ? (this.quickAddColumnKey || '') : '',
        S00USR:   this.groupBy === 'USUARIO' ? (this.quickAddColumnKey || '') : '',
        PRO32COD: sprintAGuardar
      }]
    };
    this.dataService.ConsultarWS(this.urlQuickAdd, JSON.stringify(body)).subscribe(
      () => { this.cancelarQuickAdd(); this.cargarSnapshot(); },
      (err: any) => Swal.fire('Error', err || 'No se pudo crear la tarjeta.', 'error')
    );
  }


  // ===================================================================
  // Agregar / quitar tarjeta del tablero
  // ===================================================================

  abrirModalAttach() {
    if (this.permisos.ATTACH_CARD !== 'S') return;
    this.modalAttachVisible = true;
    this.modalAttachQ = '';
    this.modalAttachResults = [];
    this.buscarCardsDisponibles();
  }

  cerrarModalAttach() {
    this.modalAttachVisible = false;
    this.modalAttachResults = [];
  }

  buscarCardsDisponibles() {
    this.modalAttachBuscando = true;
    const body = {
      LLAVES: [{
        PRO21COD: this.PRO21COD,
        q:        this.modalAttachQ.trim(),
        PRO01COD: this.modalAttachSoloProy ? this.PRO01COD : '',
        limit:    100
      }]
    };
    this.dataService.ConsultarWS(this.urlBuscar, JSON.stringify(body)).subscribe(
      (resp: any) => {
        this.modalAttachResults = (resp?.DATA || []) as CardDisponible[];
        this.modalAttachBuscando = false;
      },
      (err: any) => {
        this.modalAttachBuscando = false;
        Swal.fire('Error', err || 'No se pudo buscar tarjetas.', 'error');
      }
    );
  }

  attachCard(card: CardDisponible) {
    const body = { LLAVES: [{ PRO21COD: this.PRO21COD, PRO23COD: card.PRO23COD }] };
    this.dataService.ConsultarWS(this.urlAttach, JSON.stringify(body)).subscribe(
      () => {
        this.modalAttachResults = this.modalAttachResults.filter(c => c.PRO23COD !== card.PRO23COD);
        this.cargarSnapshot();
      },
      (err: any) => Swal.fire('Error', err || 'No se pudo agregar la tarjeta.', 'error')
    );
  }

  detachCard(card: TarjetaBoard) {
    if (this.permisos.DETACH_CARD !== 'S') return;
    Swal.fire({
      title: 'Quitar del tablero',
      text:  'La tarjeta PRO-' + card.PRO23COD + ' se quitara solo de este tablero. La tarjeta NO se elimina.',
      icon: 'question', showCancelButton: true
    }).then(r => {
      if (!r.isConfirmed) return;
      const body = { LLAVES: [{ PRO21COD: this.PRO21COD, PRO23COD: card.PRO23COD }] };
      this.dataService.ConsultarWS(this.urlDetach, JSON.stringify(body)).subscribe(
        () => this.cargarSnapshot(),
        (err: any) => Swal.fire('Error', err || 'No se pudo quitar la tarjeta.', 'error')
      );
    });
  }


  // ===================================================================
  // Sprint
  // ===================================================================

  iniciarSprint(cod: string) {
    if (!cod) return;
    Swal.fire({
      title: 'Iniciar sprint', text: 'Solo puede haber un sprint activo. Los otros se cerraran.',
      icon: 'question', showCancelButton: true
    }).then(r => {
      if (!r.isConfirmed) return;
      const body = { LLAVES: [{ PRO01COD: this.PRO01COD, PRO32COD: cod }] };
      this.dataService.ConsultarWS(this.urlIniciar, JSON.stringify(body)).subscribe(
        () => { this.PRO32COD = cod; this.cargarSnapshot(); },
        (err: any) => Swal.fire('Error', err || 'No se pudo iniciar el sprint.', 'error')
      );
    });
  }

  cerrarSprintActual() {
    if (!this.sprintActual?.PRO32COD || this.sprintActual.PRO32COD === '' || this.sprintActual.PRO32COD === '-1') {
      Swal.fire('Atencion', 'No hay un sprint activo seleccionado.', 'warning');
      return;
    }
    Swal.fire({
      title: 'Cerrar sprint',
      html:  'Se tomara snapshot final y las tarjetas no terminadas pasaran al backlog. ' +
             '<br>Confirmar el cierre del sprint <b>' + this.sprintActual.PRO32NOM + '</b>?',
      icon: 'warning', showCancelButton: true
    }).then(r => {
      if (!r.isConfirmed) return;
      const body = { LLAVES: [{ PRO01COD: this.PRO01COD, PRO32COD: this.sprintActual.PRO32COD }] };
      this.dataService.ConsultarWS(this.urlCerrar, JSON.stringify(body)).subscribe(
        (resp: any) => {
          Swal.fire('Realizado', resp?.DATA || 'Sprint cerrado.', 'success');
          this.PRO32COD = '';
          this.cargarSnapshot();
        },
        (err: any) => Swal.fire('Error', err || 'No se pudo cerrar el sprint.', 'error')
      );
    });
  }


  // ===================================================================
  // Helpers para los templates
  // ===================================================================

  wipExcedido(col: ColumnaBoard): boolean {
    if (col.wip == null) return false;
    return this.cuentaCol(col) > col.wip;
  }

  cuentaCol(col: ColumnaBoard): number {
    return this.cardsDeColumna(col.key).length;
  }

  spCol(col: ColumnaBoard): number {
    return this.cardsDeColumna(col.key).reduce((acc, t) => acc + (t.PRO23SP || 0), 0);
  }

  prioridadClase(p: string): string {
    if (p === 'A') return 'pb-prio-A';
    if (p === 'B') return 'pb-prio-B';
    return 'pb-prio-M';
  }

  prioridadIcon(p: string): string {
    if (p === 'A') return '🔴';
    if (p === 'B') return '🟢';
    return '🟡';
  }

  diasRestantes(fechaFin: string): string {
    if (!fechaFin) return '';
    const fin = new Date(fechaFin);
    const hoy = new Date();
    const diff = Math.ceil((fin.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));
    if (diff < 0)  return Math.abs(diff) + 'd vencido';
    if (diff === 0) return 'Termina hoy';
    return diff + 'd restantes';
  }

  porcentajeCommit(): number {
    const cap = this.sprintActual?.PRO32CAP || 0;
    const com = this.sprintActual?.PRO32SPC || 0;
    if (!cap) return 0;
    return Math.min(100, Math.round((com / cap) * 100));
  }

  inicialesUsuario(usr: string): string {
    if (!usr) return '?';
    return usr.substring(0, 2).toUpperCase();
  }

  colorUsuario(usr: string): string {
    if (!usr) return '#7a869a';
    const palette = ['#e11d48', '#d97706', '#10b981', '#0ea5e9', '#8b5cf6', '#06b6d4', '#f97316'];
    let h = 0;
    for (let i = 0; i < usr.length; i++) h = (h * 31 + usr.charCodeAt(i)) >>> 0;
    return palette[h % palette.length];
  }
  trackByCol  = (_: number, c: ColumnaBoard): string => c.key;
  trackByCard = (_: number, c: TarjetaBoard): number => c.PRO23COD;


  // ===================================================================
  // Cancelar
  // ===================================================================

  emitirCancelar() {
    this.cancelar.emit();
  }
}
